// Browser-side auto-fixer for common, safe packaging issues.
//
// Runs entirely in the browser with @zip.js/zip.js — no R2 round-trip and no
// server state. The user still re-uploads the fixed zip to confirm the
// report comes back clean; this just saves them manual rename/cleanup work.
//
// Fixes applied (idempotent, safe to run on already-clean archives):
//
//   - Strip a single common top-level wrapper folder, e.g. collapsing
//     `ghastbusters/Content/...` back to `Content/...`. Canonical
//     Marketplace layout must live at the archive root.
//   - Enforce the canonical root layout: after any wrapper strip, every
//     entry must live under `Content/`, `Marketing Art/`, or
//     `Store Art/`. Anything outside those three folders at the root is
//     dropped from the rebuilt archive.
//   - Delete any packaged-format files (`*.mcpack` / `*.mcaddon` /
//     `*.mcworld` / `*.mctemplate`) anywhere in the tree.
//   - Delete foreign-format files Bedrock doesn't use (`*.mcmeta` Java
//     metadata, `*.hdr` Radiance images).
//   - Delete macOS `.DS_Store`, macOS AppleDouble sidecars (`._*`), any
//     `__MACOSX/` folder, Windows `Thumbs.db`, and stray `*.map`
//     source-map files anywhere in the tree.
//   - Strip leading byte-order marks (UTF-8 / UTF-16) from `.json` files
//     so Minecraft's strict JSON parser stops choking on U+FEFF.
//   - Canonicalise mixed screenshot spellings: if BOTH `_screenshot_N` and
//     `_StoreScreenshot_N` appear, rename the `_StoreScreenshot_` ones to
//     `_screenshot_` so only one style ships.
//   - Canonicalise mixed panorama spellings: if BOTH `_panorama.jpg` and
//     `_panorama_0.jpg` appear, rename the bare `_panorama.jpg` to
//     `_panorama_0.jpg`.
//   - Add the `_slim` / `_custom` model tag to skin PNGs whose filenames
//     are missing one, based on skins.json:skins[].geometry.
//   - Normalise the `<contentname>` prefix on Store Art / Marketing Art
//     files when the suffix is correct but the prefix doesn't match the
//     package's canonical name (sourced from levelname.txt for worlds).
//
// Not attempted: adding missing required files, editing manifest contents,
// or correcting invalid manifest types — those require human judgement.

import {
  BlobReader,
  BlobWriter,
  configure,
  TextWriter,
  Uint8ArrayReader,
  Uint8ArrayWriter,
  ZipReader,
  ZipWriter,
  type Entry,
} from "@zip.js/zip.js";

// Cloudflare Workers forbid nested workers; the browser doesn't, but we
// disable them anyway for smaller bundle size and deterministic behaviour.
configure({ useWebWorkers: false });

export interface FixResult {
  /** The rebuilt archive as a downloadable Blob. */
  blob: Blob;
  /** Suggested filename for the download (<original>-fixed.zip). */
  filename: string;
  /** Human-readable list of fixes actually applied. */
  appliedFixes: string[];
}

export async function fixZip(file: File): Promise<FixResult> {
  const reader = new ZipReader(new BlobReader(file));
  let entries: Entry[];
  try {
    entries = await reader.getEntries();
  } catch (err) {
    await reader.close().catch(() => {});
    throw err;
  }

  // Normalise path separators up front so all later logic can assume `/`.
  const normalised = entries.map((entry) => ({
    entry,
    path: entry.filename.replace(/\\/g, "/"),
  }));

  const wrapper = detectWrapperFolder(normalised);
  if (wrapper) {
    for (const n of normalised) n.path = n.path.slice(wrapper.length);
  }

  const mixedScreenshots = detectMixedScreenshotSpellings(normalised);
  const mixedPanorama = detectMixedPanoramaSpellings(normalised);

  // Work out which skin PNGs need a model tag appended based on the skin's
  // geometry declaration in skins.json. Also produces an updated skins.json
  // body so the `texture` references stay in sync after the rename.
  const skinFix = await computeSkinModelTagRenames(normalised);

  // Detect the canonical contentname for the package, then rename Store Art
  // / Marketing Art files whose suffix is recognised but prefix is wrong.
  const canonicalName = await findCanonicalContentName(normalised);
  const artRenames = canonicalName
    ? computeArtPrefixRenames(normalised, canonicalName)
    : new Map<string, string>();

  // Compute Marketing Art screenshot index renames against the paths the
  // archive will have *after* art-prefix renaming, so the index pass
  // operates on the canonicalised filenames rather than the originals.
  const projectedPaths = normalised.map(({ path }) => artRenames.get(path) ?? path);
  const screenshotRenames = computeMarketingScreenshotRenames(projectedPaths);

  // Persona / emote submissions may legitimately ship a .mcpack
  // inside Marketing Art/. We detect them by folder presence — same
  // signal the server-side detector uses — and use that to decide
  // whether the .mcpack stripper preserves Marketing Art entries.
  const isPersonaOrEmote = normalised.some(({ path }) =>
    /^content\/(persona|emotes)\//i.test(path),
  );

  const writer = new ZipWriter(new BlobWriter("application/zip"));
  const applied = new Set<string>();

  try {
    for (const { entry, path: rawPath } of normalised) {
      if (entry.directory) continue; // zip.js writes directory entries implicitly

      let path = rawPath;
      if (wrapper) applied.add(`Stripped wrapper folder "${wrapper.slice(0, -1)}/"`);

      // Canonical root layout: only Content / Marketing Art / Store Art are
      // allowed at the archive root. Anything else — loose files, stray
      // folders like `docs/`, extracted sample archives — gets dropped so
      // the rebuilt zip matches what Marketplace ingest expects.
      if (!isUnderCanonicalRoot(path)) {
        applied.add(
          `Removed non-canonical root entry: ${rootSegment(path)}`,
        );
        continue;
      }

      // Junk-file removal. We skip these entries entirely. .mcaddon /
      // .mcworld / .mctemplate are never allowed; .mcpack is only
      // allowed inside Marketing Art/ on persona / emote submissions
      // (see pack.no-mcpack-* rules), so the .mcpack branch checks
      // both the folder and the detected product type.
      const base = path.slice(path.lastIndexOf("/") + 1);
      if (/\.(mcaddon|mcworld|mctemplate)$/i.test(base)) {
        applied.add(
          "Removed packaged-format files (.mcaddon / .mcworld / .mctemplate)",
        );
        continue;
      }
      if (/\.mcpack$/i.test(base)) {
        const inMarketingArt = /^Marketing Art\//i.test(path);
        if (!(inMarketingArt && isPersonaOrEmote)) {
          applied.add("Removed .mcpack files (only allowed inside Marketing Art/ for persona / emote)");
          continue;
        }
      }
      if (/\.(mcmeta|hdr)$/i.test(base)) {
        applied.add(
          "Removed foreign-format files (.mcmeta Java metadata, .hdr Radiance images)",
        );
        continue;
      }
      if (/\.dat_old$/i.test(base)) {
        applied.add("Removed .dat_old Bedrock backup files (e.g. level.dat_old)");
        continue;
      }
      // Persona / emote submissions are exempt from these strips.
      // Everything else: drop nested .zip and .bbmodel anywhere, and
      // .psd inside Content/ (Marketing Art/.psd is intentionally
      // preserved — it's a valid keyart / partnerart authoring source).
      if (!isPersonaOrEmote) {
        if (/\.zip$/i.test(base)) {
          applied.add("Removed nested .zip files");
          continue;
        }
        if (/\.bbmodel$/i.test(base)) {
          applied.add("Removed Blockbench project files (.bbmodel)");
          continue;
        }
        if (/\.psd$/i.test(base) && /^Content\//i.test(path)) {
          applied.add("Removed Photoshop project files (.psd) from Content/");
          continue;
        }
      }
      if (base === ".DS_Store") {
        applied.add("Removed macOS .DS_Store files");
        continue;
      }
      if (base.startsWith("._")) {
        // macOS AppleDouble resource-fork sidecars. Microsoft's Marketplace
        // validator rejects these as "extraneous files".
        applied.add("Removed macOS AppleDouble sidecar files (._*)");
        continue;
      }
      if (path === "__MACOSX" || path.startsWith("__MACOSX/")) {
        applied.add("Removed __MACOSX/ folder");
        continue;
      }
      if (base === "Thumbs.db") {
        applied.add("Removed Windows Thumbs.db files");
        continue;
      }
      if (/\.map$/i.test(base)) {
        applied.add("Removed source map (*.map) files");
        continue;
      }

      if (mixedScreenshots) {
        const renamed = path.replace(/_StoreScreenshot_/g, "_screenshot_");
        if (renamed !== path) {
          applied.add("Renamed _StoreScreenshot_ → _screenshot_");
          path = renamed;
        }
      }

      if (mixedPanorama) {
        const renamed = path.replace(/_panorama\.(jpe?g)$/i, "_panorama_0.$1");
        if (renamed !== path) {
          applied.add("Renamed _panorama.<ext> → _panorama_0.<ext>");
          path = renamed;
        }
      }

      // Skin texture rename (e.g. `Parrot.png` → `Parrot_slim.png`) based
      // on the model derived from skins.json:skins[].geometry.
      const renamedSkin = skinFix.renameMap.get(rawPath);
      if (renamedSkin) {
        applied.add(
          `Added model tag to skin texture: ${basename(rawPath)} → ${basename(renamedSkin)}`,
        );
        path = renamedSkin;
      }

      // Store Art / Marketing Art prefix rename — suffix was correct but the
      // <contentname> didn't match the package's canonical name.
      const renamedArt = artRenames.get(path) ?? artRenames.get(rawPath);
      if (renamedArt) {
        applied.add(
          `Renamed ${basename(path)} → ${basename(renamedArt)} to match canonical contentname "${canonicalName}"`,
        );
        path = renamedArt;
      }

      // Marketing screenshot index normalisation. After the prefix
      // rename above the basename is canonical, so we look up the
      // renumber map keyed on the (post-prefix) path and apply.
      const renamedShot = screenshotRenames.get(path);
      if (renamedShot) {
        applied.add(
          `Renumbered marketing screenshot: ${basename(path)} → ${basename(renamedShot)}`,
        );
        path = renamedShot;
      }

      if (typeof entry.getData !== "function") continue; // defensive — non-file entry

      // When we rewrote skins.json's texture references, write the updated
      // JSON instead of copying the original entry.
      if (
        skinFix.updatedSkinsJson !== undefined &&
        skinFix.skinsJsonPath === rawPath
      ) {
        const bytes = new TextEncoder().encode(skinFix.updatedSkinsJson);
        await writer.add(path, new Uint8ArrayReader(bytes));
        applied.add("Updated skins.json texture references to match renamed PNGs");
        continue;
      }

      const rawBytes = await entry.getData(new Uint8ArrayWriter());
      // Cast to the loose Uint8Array type accepted by Uint8ArrayReader; the
      // DOM typings distinguish `Uint8Array<ArrayBuffer>` from
      // `Uint8Array<ArrayBufferLike>` and subarray() widens to the latter.
      let data: Uint8Array = rawBytes;

      // Strip a leading BOM from every .json entry. Minecraft's parser
      // bails on a U+FEFF prefix; some Windows editors add one silently.
      if (/\.json$/i.test(base)) {
        const stripped = stripBom(data);
        if (stripped !== data) {
          applied.add("Stripped leading byte-order mark (BOM) from JSON files");
          data = stripped;
        }
      }

      await writer.add(path, new Uint8ArrayReader(data as Uint8Array<ArrayBuffer>));
    }
  } finally {
    await reader.close().catch(() => {});
  }

  const blob = await writer.close();
  const originalStem = file.name.replace(/\.zip$/i, "") || "package";

  return {
    blob,
    filename: `${originalStem}-fixed.zip`,
    appliedFixes: Array.from(applied),
  };
}

/** Top-level folder names that are allowed to appear at the archive root.
 *  Matched case-insensitively. Kept in sync with the `pack.root-layout`
 *  rule in shared/src/rules/common.ts. */
const CANONICAL_ROOT_FOLDERS = ["Content", "Marketing Art", "Store Art"] as const;
const CANONICAL_ROOT_LOWER = new Set(
  CANONICAL_ROOT_FOLDERS.map((f) => f.toLowerCase()),
);

function rootSegment(path: string): string {
  const slash = path.indexOf("/");
  return slash === -1 ? path : path.slice(0, slash);
}

function isUnderCanonicalRoot(path: string): boolean {
  return CANONICAL_ROOT_LOWER.has(rootSegment(path).toLowerCase());
}

/**
 * Return a wrapper path prefix (including trailing slash) to strip from
 * every entry, or null if the archive already lives at the right level.
 *
 * Two detection strategies:
 *   1. Canonical: if *any* entry's path contains one of the canonical
 *      folders as a path segment (`.../Content/...` or `.../Marketing
 *      Art/...` or `.../Store Art/...`) at the SAME depth across the
 *      entries that hit that folder, the common prefix up to that depth
 *      is the wrapper. This handles the `ghastbusters/Content/...` case
 *      as well as arbitrary wrappers so long as at least one canonical
 *      folder lives underneath.
 *   2. Fallback: the original "every entry shares a single top-level
 *      directory" heuristic, in case an archive is missing the canonical
 *      folders entirely (e.g. a broken submission). We still strip so
 *      the user can see the inner layout in the rebuilt file.
 *
 * Filesystem junk (`__MACOSX/`, `._*` sidecars, `.DS_Store`, `Thumbs.db`,
 * `*.map`) is excluded from detection entirely. Otherwise a Finder-zipped
 * pack would see its own `__MACOSX/Content/...` sidecar tree as "a
 * wrapper that contains Content/" and mis-strip the real root — which
 * was exactly the bug that made the auto-fixer delete all real content
 * on macOS-produced archives.
 */
function detectWrapperFolder(items: { path: string }[]): string | null {
  const relevant = items.filter((i) => !isJunkPath(i.path));
  if (relevant.length === 0) return null;
  for (const folder of CANONICAL_ROOT_FOLDERS) {
    const wrapper = detectWrapperForFolder(relevant, folder);
    if (wrapper !== null) return wrapper;
  }
  return detectCommonSingleWrapper(relevant);
}

function isJunkPath(path: string): boolean {
  const lower = path.toLowerCase();
  if (lower === "__macosx" || lower.startsWith("__macosx/")) return true;
  const base = path.slice(path.lastIndexOf("/") + 1);
  if (base === ".DS_Store") return true;
  if (base.startsWith("._")) return true;
  if (base === "Thumbs.db") return true;
  if (/\.map$/i.test(base)) return true;
  return false;
}

function detectWrapperForFolder(
  items: { path: string }[],
  folderName: string,
): string | null {
  const needle = "/" + folderName.toLowerCase() + "/";
  const prefixes = new Set<string>();
  for (const { path } of items) {
    if (!path) continue;
    const lower = "/" + path.toLowerCase();
    const hit = lower.indexOf(needle);
    if (hit === -1) continue; // folder name not present at all
    // When hit === 0 the folder is already at the archive root; record
    // an empty prefix so a legit archive with `Content/` at root
    // reports "no wrapper" rather than picking up a sibling like
    // `__MACOSX/Content/...` as the only detected prefix.
    prefixes.add(path.slice(0, hit));
  }
  if (prefixes.size !== 1) return null;
  const [prefix] = prefixes;
  return prefix ? prefix : null;
}

function detectCommonSingleWrapper(items: { path: string }[]): string | null {
  let wrapper: string | null = null;
  for (const { path } of items) {
    if (!path) continue;
    const slash = path.indexOf("/");
    if (slash === -1) return null;
    const first = path.slice(0, slash);
    if (wrapper === null) wrapper = first;
    else if (wrapper !== first) return null;
  }
  return wrapper ? wrapper + "/" : null;
}

function detectMixedScreenshotSpellings(items: { path: string }[]): boolean {
  const hasShort = items.some((i) => /(?:^|\/)Store Art\/.*_screenshot_.*\.(?:jpe?g)$/i.test(i.path));
  const hasStore = items.some((i) =>
    /(?:^|\/)Store Art\/.*_StoreScreenshot_.*\.(?:jpe?g)$/i.test(i.path),
  );
  return hasShort && hasStore;
}

function detectMixedPanoramaSpellings(items: { path: string }[]): boolean {
  const hasBare = items.some((i) => /(?:^|\/)Store Art\/.*_panorama\.(?:jpe?g)$/i.test(i.path));
  const hasIndexed = items.some((i) =>
    /(?:^|\/)Store Art\/.*_panorama_0\.(?:jpe?g)$/i.test(i.path),
  );
  return hasBare && hasIndexed;
}

/** Accepted model-tag tokens (prefix or suffix, separated by `_`). */
const MODEL_TAG_BASENAME =
  /^(?:(?:a|alex|slim|customSlim|s|steve|custom)_.+|.+_(?:a|alex|slim|customSlim|s|steve|custom))\.png$/i;

interface SkinFixResult {
  /** Map of old archive path → new archive path (both post-wrapper-strip). */
  renameMap: Map<string, string>;
  /** Rewritten skins.json body, or undefined if no changes were needed. */
  updatedSkinsJson?: string;
  /** Archive path of skins.json so the main loop can substitute it. */
  skinsJsonPath?: string;
}

/**
 * Walks skins.json and, for each skin whose texture filename lacks a model
 * tag, derives the right tag from the skin's `geometry` field and emits a
 * rename. We canonicalise on `_slim` and `_custom` suffixes — those are the
 * two tokens the rule set clearly maps to a model.
 *
 * Safeguards:
 *   - skips textures that already have a valid model tag,
 *   - skips skins whose geometry doesn't clearly identify slim vs custom,
 *   - only renames PNGs that actually exist in the archive.
 *
 * Returns an empty rename map when nothing needs changing, which lets the
 * caller skip rewriting skins.json.
 */
async function computeSkinModelTagRenames(
  normalised: Array<{ entry: Entry; path: string }>,
): Promise<SkinFixResult> {
  const skinsJsonItem = normalised.find((i) =>
    /^content\/skin_pack\/skins\.json$/i.test(i.path),
  );
  if (!skinsJsonItem) return { renameMap: new Map() };

  // The `in` check narrows the Entry union to the FileEntry variant.
  const skinsEntry = skinsJsonItem.entry;
  if (!("getData" in skinsEntry)) return { renameMap: new Map() };

  let skinsData: unknown;
  try {
    const text = await skinsEntry.getData(new TextWriter());
    skinsData = JSON.parse(stripBomText(text));
  } catch {
    return { renameMap: new Map() };
  }

  if (!skinsData || typeof skinsData !== "object") return { renameMap: new Map() };
  const skinsObj = skinsData as { skins?: unknown };
  if (!Array.isArray(skinsObj.skins)) return { renameMap: new Map() };

  // skins.json sits at the pack root; texture values are relative to it.
  const packFolder = skinsJsonItem.path.slice(
    0,
    skinsJsonItem.path.lastIndexOf("/") + 1,
  );

  const existing = new Set(normalised.map((n) => n.path.toLowerCase()));
  const renameMap = new Map<string, string>();
  let changed = false;

  for (const raw of skinsObj.skins) {
    if (!raw || typeof raw !== "object") continue;
    const skin = raw as { texture?: unknown; geometry?: unknown };
    const texture = skin.texture;
    if (typeof texture !== "string") continue;

    // Basename check so `subfolder/Parrot_slim.png` is treated as already tagged.
    const textureBase = texture.slice(texture.lastIndexOf("/") + 1);
    if (MODEL_TAG_BASENAME.test(textureBase)) continue;
    if (!/\.png$/i.test(textureBase)) continue;

    const geometry = typeof skin.geometry === "string" ? skin.geometry : "";
    let tag: "slim" | "custom" | null = null;
    if (/slim/i.test(geometry)) tag = "slim";
    else if (/custom/i.test(geometry)) tag = "custom";
    if (!tag) continue;

    const oldPath = packFolder + texture;
    if (!existing.has(oldPath.toLowerCase())) continue; // texture PNG missing — skip

    const stem = texture.replace(/\.png$/i, "");
    const newTexture = `${stem}_${tag}.png`;
    const newPath = packFolder + newTexture;

    renameMap.set(oldPath, newPath);
    skin.texture = newTexture;
    changed = true;
  }

  if (!changed) return { renameMap: new Map() };

  return {
    renameMap,
    updatedSkinsJson: JSON.stringify(skinsData, null, 2),
    skinsJsonPath: skinsJsonItem.path,
  };
}

function basename(path: string): string {
  return path.slice(path.lastIndexOf("/") + 1);
}

/**
 * Return `data` with any leading UTF-8 / UTF-16 BOM removed. Returns the
 * original reference when no BOM is present so callers can no-op quickly.
 */
function stripBom(data: Uint8Array): Uint8Array {
  if (data.length >= 3 && data[0] === 0xef && data[1] === 0xbb && data[2] === 0xbf) {
    return data.subarray(3);
  }
  if (data.length >= 2 && data[0] === 0xff && data[1] === 0xfe) {
    return data.subarray(2);
  }
  if (data.length >= 2 && data[0] === 0xfe && data[1] === 0xff) {
    return data.subarray(2);
  }
  return data;
}

/**
 * Strip the Unicode BOM character (U+FEFF) from the start of a decoded
 * string. zip.js's TextWriter decodes bytes to a JS string, where a BOM
 * shows up as a single `\uFEFF` code unit; JSON.parse refuses to parse
 * a document starting with it.
 */
function stripBomText(text: string): string {
  return text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
}

// ---------- Canonical contentname + art-prefix renaming ----------

/**
 * Recognised Store Art / Marketing Art suffix patterns. When a file matches
 * one of these but its `<contentname>_` prefix doesn't line up with the
 * package's canonical name, we auto-rename it.
 */
const ART_SUFFIX_RE =
  /^(Store Art|Marketing Art)\/(.+?)(_Thumbnail_0|_packicon_0|_panorama(?:_\d+)?|_(?:screenshot|StoreScreenshot)_\d+|_MarketingKeyArt|_PartnerArt|_MarketingScreenshot_\d+)\.(jpg|jpeg|psd)$/i;

/**
 * Look up the package's canonical contentname:
 *   content/world_template/levelname.txt — trimmed contents
 *
 * Used to canonicalise art file prefixes when the suffix is right but
 * the `<contentname>_` portion doesn't match the package's official
 * name. World templates are the only product type with a "canonical
 * name" source we can trust: `levelname.txt` carries the displayed
 * world name and Marketplace expects every Store / Marketing Art file
 * to use that exact name as a prefix.
 *
 * Skin packs are deliberately NOT canonicalised. We used to rename art
 * files to `skins.json:serialize_name`, but that value is an internal
 * identifier (it feeds `pack.<serialize_name>` localisation keys), not
 * the submission's content name — a pack with serialize_name
 * "colorful_teams" legitimately ships "Colorful Teams_Thumbnail_0.jpg".
 * The skin art rules only check the `_<Suffix>` part of the filename.
 *
 * Resource and behavior packs are likewise NOT canonicalised. We used
 * to fall back to `header.name` from BP/RP manifests, but that field is
 * now required to be the literal localization key `pack.name` (per
 * `pack.manifest-name-localized`), and even when authors leave a real
 * name in there it may include spaces, version numbers, or other
 * strings that don't belong in art filenames. The creator's chosen
 * prefix on Store Art / Marketing Art files is left alone for those
 * product types.
 *
 * Returns null when no source is available, which leaves the prefix
 * rename map empty.
 */
async function findCanonicalContentName(
  normalised: Array<{ entry: Entry; path: string }>,
): Promise<string | null> {
  const readText = async (entry: Entry): Promise<string | null> => {
    if (!("getData" in entry)) return null;
    try {
      return await entry.getData(new TextWriter());
    } catch {
      return null;
    }
  };

  const level = normalised.find((n) =>
    /^content\/world_template\/levelname\.txt$/i.test(n.path),
  );
  if (level) {
    const text = await readText(level.entry);
    if (text) {
      const trimmed = stripBomText(text).replace(/\r?\n/g, "").trim();
      if (trimmed) return trimmed;
    }
  }

  return null;
}

/**
 * For each Store Art / Marketing Art file whose suffix is recognised but
 * whose `<prefix>_` doesn't match `canonical`, emit a rename. Skips files
 * whose target name already exists so we don't clobber a legitimate asset.
 */
/**
 * Renumber Marketing Art screenshots so the indices form a 0-indexed
 * run with no gaps, regardless of the source numbering. A pack with
 * `_01, _02, _03, _04, _05` becomes `_0, _1, _2, _3, _4`; a pack with
 * sparse indices like `_2, _5, _9` becomes `_0, _1, _2`.
 *
 * Files are grouped by their `<contentname>_` prefix (case-insensitive)
 * so a stray screenshot belonging to a different pack doesn't get its
 * counter mixed in. The map returned is keyed by the path the file
 * has AFTER any art-prefix rename has already been applied — callers
 * should pass projected paths.
 */
function computeMarketingScreenshotRenames(paths: string[]): Map<string, string> {
  const SCREENSHOT_RE =
    /^(Marketing Art\/)(.+)_MarketingScreenshot_(\d+)(\.(?:png|jpe?g|psd))$/i;
  type Match = {
    path: string;
    folder: string;
    prefix: string;
    index: number;
    ext: string;
  };
  const matches: Match[] = [];
  for (const path of paths) {
    const m = SCREENSHOT_RE.exec(path);
    if (!m) continue;
    matches.push({
      path,
      folder: m[1]!,
      prefix: m[2]!,
      index: parseInt(m[3]!, 10),
      ext: m[4]!,
    });
  }
  if (matches.length === 0) return new Map();

  const byPrefix = new Map<string, Match[]>();
  for (const entry of matches) {
    const key = entry.prefix.toLowerCase();
    const list = byPrefix.get(key) ?? [];
    list.push(entry);
    byPrefix.set(key, list);
  }

  const renames = new Map<string, string>();
  for (const group of byPrefix.values()) {
    // Stable sort: by current index ascending. Files with identical
    // indices keep their relative order via a secondary path tiebreak.
    group.sort((a, b) => a.index - b.index || a.path.localeCompare(b.path));
    for (let i = 0; i < group.length; i += 1) {
      const file = group[i]!;
      const newPath = `${file.folder}${file.prefix}_MarketingScreenshot_${i}${file.ext}`;
      if (file.path !== newPath) renames.set(file.path, newPath);
    }
  }
  return renames;
}

function computeArtPrefixRenames(
  normalised: Array<{ entry: Entry; path: string }>,
  canonical: string,
): Map<string, string> {
  const existing = new Set(normalised.map((n) => n.path.toLowerCase()));
  const renameMap = new Map<string, string>();
  const canonicalLower = canonical.toLowerCase();

  for (const { path } of normalised) {
    const m = ART_SUFFIX_RE.exec(path);
    if (!m) continue;
    const [, folder, prefix, suffix, ext] = m as unknown as [
      string,
      string,
      string,
      string,
      string,
    ];
    if (prefix.toLowerCase() === canonicalLower) continue;

    const newPath = `${folder}/${canonical}${suffix}.${ext}`;
    if (existing.has(newPath.toLowerCase())) continue; // target already exists
    renameMap.set(path, newPath);
  }
  return renameMap;
}

/** Trigger a browser download of a Blob with the given filename. */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.append(a);
  a.click();
  a.remove();
  // Let the download start before we release the URL.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
